/**
 * Project store — manages the list of projects the authenticated user can see.
 *
 * Architecture (v3.4.1):
 * - The API is the single source of truth for project visibility (enforced by
 *   scope-middleware). This store mirrors the API result and persists it to
 *   localStorage for fast subsequent page loads.
 * - On login/logout, authService dispatches the 'sn:auth-change' CustomEvent,
 *   which triggers a full re-sync of this store. This guarantees fresh state
 *   without requiring a hard page reload.
 * - No hardcoded project seeds — a brand-new user with no project assignments
 *   sees an empty list (which is the correct reflection of their access scope).
 * - Archived-locally projects (not present in the API response but marked
 *   archived on this browser) are preserved so users don't lose their
 *   archive view.
 *
 * @module stores/projectStore
 */
import { create } from 'zustand';
import type { AnalysisScope } from '../shared/analysisScope';

export interface Project {
  id: string;
  name: string;
  createdAt: string;
  archived?: boolean;
  archivedAt?: string;
  /**
   * Canonical analysis scope for this project — aircraft/system/equipment.
   * Optional for backward compatibility with pre-v3.5.0 projects.
   * Display labels are resolved via `getScopeLabel()`.
   */
  analysisScope?: AnalysisScope;
}

interface ProjectState {
  projects: Project[];
  activeProjectId: string;
  showArchived: boolean;
  isSyncing: boolean;
  hasSynced: boolean;
  getActiveProject: () => Project | undefined;
  activeProjects: () => Project[];
  archivedProjects: () => Project[];
  prefix: () => string;
  createProject: (name: string) => string;
  switchProject: (id: string) => void;
  renameProject: (id: string, name: string) => void;
  archiveProject: (id: string) => void;
  restoreProject: (id: string) => void;
  setShowArchived: (show: boolean) => void;
  syncFromApi: () => Promise<void>;
  clearAll: () => void;
}

const PROJECTS_KEY = 'sn:projects';
const ACTIVE_KEY = 'sn:active-project';
const TOKEN_KEY = 'sn:auth-token';

/** Load whatever was cached in localStorage. Returns empty list if nothing. */
function loadProjects(): Project[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return [];
    const projects: Project[] = JSON.parse(raw);
    // De-dupe by id (name collisions are legal — e.g. two tenants)
    const seen = new Set<string>();
    return projects.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  } catch {
    return [];
  }
}

function loadActiveId(projects: Project[]): string {
  const stored = localStorage.getItem(ACTIVE_KEY);
  if (stored && projects.some(p => p.id === stored && !p.archived)) return stored;
  return projects.find(p => !p.archived)?.id ?? '';
}

function save(projects: Project[], activeId: string) {
  localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
  if (activeId) localStorage.setItem(ACTIVE_KEY, activeId);
  else localStorage.removeItem(ACTIVE_KEY);
}

const initialProjects = loadProjects();
const initialActive = loadActiveId(initialProjects);

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: initialProjects,
  activeProjectId: initialActive,
  showArchived: false,
  isSyncing: false,
  hasSynced: false,

  getActiveProject: () => {
    const { projects, activeProjectId } = get();
    return projects.find(p => p.id === activeProjectId) ?? projects.find(p => !p.archived);
  },

  activeProjects: () => get().projects.filter(p => !p.archived),
  archivedProjects: () => get().projects.filter(p => p.archived),

  prefix: () => {
    const id = get().activeProjectId;
    return id ? `sn:${id}:` : 'sn:none:';
  },

  createProject: (name: string) => {
    const { projects } = get();
    if (projects.some(p => p.name === name && !p.archived)) {
      alert(`Project "${name}" already exists`);
      return '';
    }
    const id = crypto.randomUUID().slice(0, 8);
    const project: Project = { id, name, createdAt: new Date().toISOString() };
    const updated = [...projects, project];
    save(updated, id);
    set({ projects: updated, activeProjectId: id });
    return id;
  },

  switchProject: (id: string) => {
    localStorage.setItem(ACTIVE_KEY, id);
    set({ activeProjectId: id });
  },

  renameProject: (id: string, name: string) => {
    const projects = get().projects.map(p => p.id === id ? { ...p, name } : p);
    save(projects, get().activeProjectId);
    set({ projects });
  },

  archiveProject: (id: string) => {
    const { projects, activeProjectId } = get();
    const active = projects.filter(p => !p.archived && p.id !== id);
    if (active.length === 0) return; // Don't archive last active
    const updated = projects.map(p => p.id === id ? { ...p, archived: true, archivedAt: new Date().toISOString() } : p);
    const newActive = id === activeProjectId ? active[0].id : activeProjectId;
    save(updated, newActive);
    set({ projects: updated, activeProjectId: newActive });
  },

  restoreProject: (id: string) => {
    const projects = get().projects.map(p => p.id === id ? { ...p, archived: false, archivedAt: undefined } : p);
    save(projects, get().activeProjectId);
    set({ projects });
  },

  setShowArchived: (show: boolean) => set({ showArchived: show }),

  /** Clear all state and persisted cache. Used on logout. */
  clearAll: () => {
    localStorage.removeItem(PROJECTS_KEY);
    localStorage.removeItem(ACTIVE_KEY);
    set({ projects: [], activeProjectId: '', isSyncing: false, hasSynced: false });
  },

  /**
   * Sync from the API. Replaces the project list (preserving archived-local)
   * so stale entries from a previous session are pruned.
   *
   * No token -> clears state (reflects "no authenticated user sees no projects").
   * API error -> keeps existing cache (offline-friendly).
   */
  syncFromApi: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // Logged out — clear state to prevent stale visibility.
      const { projects, activeProjectId, isSyncing, hasSynced } = get();
      if (projects.length === 0 && !activeProjectId && !isSyncing && hasSynced) return;
      localStorage.removeItem(PROJECTS_KEY);
      localStorage.removeItem(ACTIVE_KEY);
      set({ projects: [], activeProjectId: '', isSyncing: false, hasSynced: true });
      return;
    }

    set({ isSyncing: true });
    try {
      const res = await fetch('/api/v1/projects', {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) {
        set({ isSyncing: false, hasSynced: true });
        return;
      }
      const json = await res.json();
      const apiProjects: Project[] = (json.data ?? []).map((p: any) => ({
        id: p.id, name: p.name, createdAt: p.createdAt,
        analysisScope: p.analysisScope ?? p.custom_fields?.analysisScope,
      }));

      // REPLACE: API is the authoritative source for visibility.
      // Keep ONLY archived-local projects not present in the API response
      // (user's personal archive view — purely client-side organizational feature).
      const { projects, activeProjectId } = get();
      const apiIds = new Set(apiProjects.map(p => p.id));
      const archivedLocalOnly = projects.filter(p => p.archived && !apiIds.has(p.id));

      const merged = [...apiProjects, ...archivedLocalOnly];
      const newActive = merged.some(p => p.id === activeProjectId && !p.archived)
        ? activeProjectId
        : merged.find(p => !p.archived)?.id ?? '';

      save(merged, newActive);
      set({ projects: merged, activeProjectId: newActive, isSyncing: false, hasSynced: true });
    } catch {
      set({ isSyncing: false, hasSynced: true });
    }
  },
}));

// ── Auth-change subscription ─────────────────────────────────────────────
// On login/register/logout, authService dispatches 'sn:auth-change'.
// Re-sync the store so the sidebar reflects the new user's visibility
// immediately (without requiring a hard page reload).
if (typeof window !== 'undefined') {
  window.addEventListener('sn:auth-change', () => {
    useProjectStore.getState().syncFromApi();
  });

  // Cross-tab consistency: if auth token changes in another tab
  // (login/logout), re-sync here too.
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === TOKEN_KEY || e.key === null) {
      useProjectStore.getState().syncFromApi();
    }
  });
}

// Initial sync on module load (first page render).
if (typeof window !== 'undefined') {
  useProjectStore.getState().syncFromApi();
}
